import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Loader2, Copy, Check, FileText, Plus, Trash2, ChevronUp, ChevronDown, Send, Bot, RefreshCw, CornerDownLeft, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    listPrevisionnels, getBpSections, saveBpSections, generateBpSections, pollBpSections,
    getCopilotHistory, resetCopilot, streamCopilot,
} from "@/lib/previsionnels";

const STAGE_LABEL = { preparation: "Préparation…", compilation: "Compilation des données financières…", redaction: "Structuration du plan par le Copilote IA (Gemini Pro)…" };
const uid = () => (crypto.randomUUID?.() || Math.random().toString(36)).replace(/-/g, "").slice(0, 12);

function toMarkdown(sections) {
    return (sections || []).map((s) => {
        let md = `## ${s.title}\n\n${(s.content || "").trim()}`;
        (s.sub_sections || []).forEach((ss) => { md += `\n\n### ${ss.title}\n\n${(ss.content || "").trim()}`; });
        return md;
    }).join("\n\n").trim();
}

export default function BusinessPlanTab({ dossierId }) {
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState("");
    const [sections, setSections] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState("");
    const [copied, setCopied] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [activeTarget, setActiveTarget] = useState(null); // { sectionId, subId|null } — cible d'insertion du Copilote
    const timer = useRef(null);
    const skipSave = useRef(true);

    useEffect(() => {
        listPrevisionnels(dossierId).then((l) => { setList(l || []); if (l?.length) setSelected((s) => s || l[0].id); }).catch(() => {});
        return () => { if (timer.current) clearInterval(timer.current); };
    }, [dossierId]);

    useEffect(() => {
        if (!selected) return;
        skipSave.current = true; setLoaded(false);
        getBpSections(selected).then((r) => { const secs = r?.sections || []; setSections(secs); setActiveTarget(secs[0] ? { sectionId: secs[0].id, subId: null } : null); setLoaded(true); }).catch(() => { setSections([]); setActiveTarget(null); setLoaded(true); });
    }, [selected]);

    // Garde la cible active valide (section non supprimée) ; retombe sur la 1ère section sinon.
    useEffect(() => {
        if (!sections.length) { if (activeTarget) setActiveTarget(null); return; }
        if (!activeTarget || !sections.some((x) => x.id === activeTarget.sectionId)) setActiveTarget({ sectionId: sections[0].id, subId: null });
    }, [sections]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sauvegarde debouncée des sections (ajout / renommage / édition / réorganisation / suppression).
    useEffect(() => {
        if (skipSave.current) { skipSave.current = false; return; }
        if (!selected || generating) return;
        const t = setTimeout(() => { saveBpSections(selected, sections).catch(() => {}); }, 1200);
        return () => clearTimeout(t);
    }, [sections, selected, generating]);

    const mutate = useCallback((fn) => setSections((prev) => fn([...prev])), []);

    function addSection() {
        mutate((s) => { s.push({ id: uid(), title: `${String(s.length + 1).padStart(2, "0")} | Nouvelle section`, content: "", order: s.length, sub_sections: [] }); return s; });
    }
    function removeSection(id) { mutate((s) => s.filter((x) => x.id !== id).map((x, i) => ({ ...x, order: i }))); }
    function moveSection(idx, dir) {
        mutate((s) => { const j = idx + dir; if (j < 0 || j >= s.length) return s; [s[idx], s[j]] = [s[j], s[idx]]; return s.map((x, i) => ({ ...x, order: i })); });
    }
    function patchSection(id, patch) { mutate((s) => s.map((x) => (x.id === id ? { ...x, ...patch } : x))); }
    function addSub(id) { mutate((s) => s.map((x) => (x.id === id ? { ...x, sub_sections: [...(x.sub_sections || []), { id: uid(), title: "Nouvelle sous-section", content: "", order: (x.sub_sections || []).length }] } : x))); }
    function patchSub(id, subId, patch) { mutate((s) => s.map((x) => (x.id === id ? { ...x, sub_sections: (x.sub_sections || []).map((ss) => (ss.id === subId ? { ...ss, ...patch } : ss)) } : x))); }
    function removeSub(id, subId) { mutate((s) => s.map((x) => (x.id === id ? { ...x, sub_sections: (x.sub_sections || []).filter((ss) => ss.id !== subId) } : x))); }

    async function generate() {
        if (!selected) return toast.error("Choisissez d'abord un scénario à analyser.");
        if (sections.length && !window.confirm("Régénérer la base écrasera les sections actuelles de ce scénario. Continuer ?")) return;
        setGenerating(true); setStage("preparation");
        try {
            const { job_id, dossier_id } = await generateBpSections(selected);
            timer.current = setInterval(async () => {
                try {
                    const s = await pollBpSections(job_id, dossier_id);
                    setStage(s.stage);
                    if (s.stage === "done") {
                        clearInterval(timer.current); setGenerating(false); skipSave.current = true; setSections(s.sections || []);
                        toast.success("Base du Business Plan générée", { description: "9 sections structurées. Affinez-les avec le Copilote." });
                    } else if (s.stage === "error") {
                        clearInterval(timer.current); setGenerating(false); toast.error("Génération impossible", { description: s.error || "Réessayez." });
                    }
                } catch (e) { clearInterval(timer.current); setGenerating(false); toast.error("Suivi interrompu", { description: e.message }); }
            }, 2500);
        } catch (e) { setGenerating(false); toast.error("Lancement impossible", { description: e.message }); }
    }

    async function exportAll() {
        try { await navigator.clipboard.writeText(toMarkdown(sections)); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Business Plan copié", { description: "Collez-le dans votre template Canva." }); }
        catch { toast.error("Copie impossible", { description: "Réessayez." }); }
    }

    const insertIntoActive = useCallback((text) => {
        const t = (text || "").trim();
        if (!t) return;
        if (!activeTarget) { toast.error("Aucune section cible", { description: "Cliquez d'abord dans la section à compléter." }); return; }
        skipSave.current = false;
        setSections((prev) => prev.map((x) => {
            if (x.id !== activeTarget.sectionId) return x;
            if (activeTarget.subId) {
                return { ...x, sub_sections: (x.sub_sections || []).map((ss) => (ss.id === activeTarget.subId ? { ...ss, content: (ss.content ? ss.content.trimEnd() + "\n\n" : "") + t } : ss)) };
            }
            return { ...x, content: (x.content ? x.content.trimEnd() + "\n\n" : "") + t };
        }));
        toast.success("Texte inséré dans la section active");
    }, [activeTarget]);

    const activeLabel = (() => {
        if (!activeTarget) return "";
        const sec = sections.find((x) => x.id === activeTarget.sectionId);
        if (!sec) return "";
        if (activeTarget.subId) { const ss = (sec.sub_sections || []).find((z) => z.id === activeTarget.subId); return ss ? `${sec.title} › ${ss.title}` : sec.title; }
        return sec.title;
    })();

    return (
        <section data-testid="business-plan-tab" className="space-y-5 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white"><FileText className="h-5 w-5" strokeWidth={1.75} /></span>
                <div className="flex-1 min-w-[220px]">
                    <h2 className="font-display text-xl text-foreground">Business Plan — Constructeur modulaire & Copilote M&A</h2>
                    <p className="text-sm text-muted-foreground">Générez la base en 9 parties, affinez chaque bloc avec le Copilote, puis exportez vers Canva.</p>
                </div>
                <Select value={selected} onValueChange={setSelected} disabled={generating || !list.length}>
                    <SelectTrigger data-testid="bp-scenario-select" className="h-11 w-60 bg-background"><SelectValue placeholder={list.length ? "Choisir un scénario" : "Aucun prévisionnel"} /></SelectTrigger>
                    <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.titre || "Sans titre"}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" data-testid="bp-generate" onClick={generate} disabled={generating || !selected} className="h-11 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                    {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" strokeWidth={1.75} />}
                    {generating ? "Génération…" : "Générer la base du Business Plan (IA)"}
                </Button>
            </div>

            {generating && (
                <div data-testid="bp-progress" className="flex items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-800">
                    <Loader2 className="h-4 w-4 animate-spin" /> {STAGE_LABEL[stage] || "Traitement…"} <span className="text-indigo-500">(jusqu'à 1 à 2 minutes)</span>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-5 items-start">
                {/* Panneau gauche 60% — Constructeur */}
                <div data-testid="bp-builder" className="xl:col-span-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sections du Business Plan</h3>
                        <Button type="button" size="sm" variant="outline" data-testid="bp-export-all" onClick={exportAll} disabled={!sections.length} className="rounded-full h-8">
                            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} Tout exporter
                        </Button>
                    </div>

                    {sections.map((s, idx) => (
                        <SectionBlock key={s.id} section={s} idx={idx} total={sections.length} active={activeTarget}
                            onActivate={(subId) => setActiveTarget({ sectionId: s.id, subId: subId || null })}
                            onMove={(d) => moveSection(idx, d)} onRemove={() => removeSection(s.id)}
                            onPatch={(p) => patchSection(s.id, p)} onAddSub={() => addSub(s.id)}
                            onPatchSub={(subId, p) => patchSub(s.id, subId, p)} onRemoveSub={(subId) => removeSub(s.id, subId)} />
                    ))}

                    <Button type="button" variant="outline" data-testid="bp-add-section" onClick={addSection} disabled={!selected}
                        className="w-full rounded-xl border-dashed h-11 text-indigo-700 hover:bg-indigo-50">
                        <Plus className="h-4 w-4" /> Ajouter une section
                    </Button>

                    {loaded && !sections.length && !generating && (
                        <div data-testid="bp-empty" className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-10 text-center text-sm text-muted-foreground">
                            Aucune section. Cliquez sur « Générer la base du Business Plan (IA) » ou ajoutez vos sections manuellement.
                        </div>
                    )}
                </div>

                {/* Panneau droit 40% — Copilote */}
                <div className="xl:col-span-2">
                    <CopilotPanel prevId={selected} sections={sections} activeLabel={activeLabel} canInsert={!!activeTarget} onInsert={insertIntoActive} />
                </div>
            </div>
        </section>
    );
}

function SectionBlock({ section, idx, total, active, onActivate, onMove, onRemove, onPatch, onAddSub, onPatchSub, onRemoveSub }) {
    const [open, setOpen] = useState(true);
    const isSectionActive = active && active.sectionId === section.id && !active.subId;
    return (
        <div data-testid={`bp-section-${idx}`} className={`rounded-xl border bg-white shadow-sm transition-colors ${isSectionActive ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`}>
            <div className="flex items-center gap-2 border-b border-slate-100 p-2.5">
                <button type="button" data-testid={`bp-section-toggle-${idx}`} onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-700">
                    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <Input data-testid={`bp-section-title-${idx}`} value={section.title} onFocus={() => onActivate(null)} onChange={(e) => onPatch({ title: e.target.value })}
                    className="h-9 flex-1 border-transparent font-semibold text-slate-800 focus:border-slate-300" />
                {isSectionActive && <span data-testid={`bp-section-active-${idx}`} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700"><Crosshair className="h-3 w-3" /> Cible</span>}
                <div className="flex items-center gap-1">
                    <button type="button" data-testid={`bp-section-up-${idx}`} onClick={() => onMove(-1)} disabled={idx === 0} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                    <button type="button" data-testid={`bp-section-down-${idx}`} onClick={() => onMove(1)} disabled={idx === total - 1} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                    <button type="button" data-testid={`bp-section-delete-${idx}`} onClick={onRemove} className="rounded p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                </div>
            </div>
            {open && (
                <div className="space-y-3 p-3">
                    <textarea data-testid={`bp-section-content-${idx}`} value={section.content || ""} onFocus={() => onActivate(null)} onChange={(e) => onPatch({ content: e.target.value })}
                        placeholder="Rédigez cette section (Markdown accepté) ou demandez un texte au Copilote →"
                        className="min-h-[130px] w-full resize-y rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-800 focus:border-primary focus:outline-none" />
                    {(section.sub_sections || []).map((ss, j) => {
                        const isSubActive = active && active.sectionId === section.id && active.subId === ss.id;
                        return (
                        <div key={ss.id} data-testid={`bp-sub-${idx}-${j}`} className={`rounded-lg border p-2.5 ml-4 transition-colors ${isSubActive ? "border-emerald-400 ring-1 ring-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"}`}>
                            <div className="flex items-center gap-2">
                                <Input data-testid={`bp-sub-title-${idx}-${j}`} value={ss.title} onFocus={() => onActivate(ss.id)} onChange={(e) => onPatchSub(ss.id, { title: e.target.value })}
                                    className="h-8 flex-1 border-transparent bg-transparent text-sm font-medium focus:border-slate-300" />
                                {isSubActive && <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700"><Crosshair className="h-3 w-3" /> Cible</span>}
                                <button type="button" data-testid={`bp-sub-delete-${idx}-${j}`} onClick={() => onRemoveSub(ss.id)} className="rounded p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                            <textarea data-testid={`bp-sub-content-${idx}-${j}`} value={ss.content || ""} onFocus={() => onActivate(ss.id)} onChange={(e) => onPatchSub(ss.id, { content: e.target.value })}
                                className="mt-2 min-h-[80px] w-full resize-y rounded-lg border border-slate-200 bg-white p-2.5 text-sm leading-relaxed text-slate-800 focus:border-primary focus:outline-none" />
                        </div>
                        );
                    })}
                    <Button type="button" size="sm" variant="ghost" data-testid={`bp-add-sub-${idx}`} onClick={onAddSub} className="ml-4 h-8 text-indigo-700 hover:bg-indigo-50">
                        <Plus className="h-3.5 w-3.5" /> Ajouter une sous-section
                    </Button>
                </div>
            )}
        </div>
    );
}

function CopilotPanel({ prevId, sections, activeLabel, canInsert, onInsert }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scroller = useRef(null);

    useEffect(() => {
        if (!prevId) { setMessages([]); return; }
        getCopilotHistory(prevId).then((r) => setMessages(r?.messages || [])).catch(() => setMessages([]));
    }, [prevId]);

    useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

    async function send() {
        const text = input.trim();
        if (!text || !prevId || busy) return;
        setInput(""); setBusy(true);
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        await streamCopilot(prevId, text, sections, {
            onDelta: (d) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d }; return c; }),
            onDone: () => setBusy(false),
            onError: (err) => { setBusy(false); setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: `⚠️ ${err}` }; return c; }); toast.error("Copilote indisponible", { description: err }); },
        });
    }

    async function reset() {
        if (!prevId || !window.confirm("Effacer la conversation avec le Copilote ?")) return;
        try { await resetCopilot(prevId); setMessages([]); toast.success("Conversation réinitialisée"); } catch (e) { toast.error("Échec", { description: e.message }); }
    }

    return (
        <div data-testid="bp-copilot" className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm h-[72vh] sticky top-4">
            <div className="flex items-center gap-2 border-b border-slate-100 p-3.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white"><Bot className="h-4 w-4" /></span>
                <div className="flex-1">
                    <h3 className="font-semibold text-sm text-slate-800">Copilote M&A</h3>
                    <p className="text-xs text-muted-foreground">Lit vos chiffres (P&amp;L, Trésorerie, DSCR) &amp; vos sections</p>
                </div>
                <button type="button" data-testid="bp-copilot-reset" onClick={reset} disabled={!messages.length} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"><RefreshCw className="h-4 w-4" /></button>
            </div>

            {activeLabel && (
                <div data-testid="bp-copilot-target" className="flex items-center gap-1.5 border-b border-slate-100 bg-emerald-50/50 px-3.5 py-2 text-xs text-emerald-800">
                    <Crosshair className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Section cible : <strong>{activeLabel}</strong></span>
                </div>
            )}

            <div ref={scroller} data-testid="bp-copilot-messages" className="flex-1 space-y-3 overflow-y-auto p-3.5">
                {!messages.length && (
                    <div className="rounded-xl bg-slate-50 p-4 text-sm text-muted-foreground">
                        Discutez avec le Copilote pour itérer section par section. Exemple : « Rédige l'étude de marché » ou « Analyse le DSCR du scénario ».
                    </div>
                )}
                {messages.map((m, i) => (
                    <div key={i} data-testid={`bp-copilot-msg-${i}`} className={m.role === "user" ? "flex justify-end" : "flex flex-col items-start"}>
                        <div className={m.role === "user" ? "max-w-[85%] rounded-2xl rounded-br-sm bg-indigo-600 px-3.5 py-2 text-sm text-white" : "max-w-[90%] rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-2 text-sm text-slate-800"}>
                            {m.role === "assistant"
                                ? (m.content ? <div className="prose prose-slate prose-sm max-w-none"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div> : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />)
                                : m.content}
                        </div>
                        {m.role === "assistant" && m.content && !m.content.startsWith("⚠️") && (
                            <button type="button" data-testid={`bp-copilot-insert-${i}`} onClick={() => onInsert(m.content)} disabled={!canInsert}
                                className="mt-1 flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40">
                                <CornerDownLeft className="h-3.5 w-3.5" /> Insérer dans la section active
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <div className="border-t border-slate-100 p-3">
                <div className="flex items-end gap-2">
                    <textarea data-testid="bp-copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={prevId ? "Demandez une rédaction ou une analyse…" : "Sélectionnez un scénario"}
                        disabled={!prevId || busy} rows={2}
                        className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 p-2.5 text-sm focus:border-primary focus:outline-none disabled:opacity-50" />
                    <Button type="button" data-testid="bp-copilot-send" onClick={send} disabled={!prevId || busy || !input.trim()} className="h-10 w-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 p-0">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                </div>
            </div>
        </div>
    );
}
